import React, { useState } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';

function CreateMatchModal({ session, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: '',
    slot_count: 100,
    entry_fee: 10,
    mode: 'solo',
    map: 'Erangel',
    start_ts: '',
    description: '',
    format: 'TPP',
    prize_pool: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.start_ts) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate date is in future
    const startDate = new Date(formData.start_ts);
    if (startDate < new Date()) {
      toast.error('Start time must be in the future');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || session?.access_token;

      const response = await fetch(`${API_BASE}/admin/matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          slot_count: parseInt(formData.slot_count),
          entry_fee: parseInt(formData.entry_fee),
          start_ts: new Date(formData.start_ts).toISOString(),
          description: formData.description,
          format: formData.format,
          prize_pool_bigint: formData.prize_pool ? parseInt(formData.prize_pool) * 100 : 0,
          per_kill: formData.per_kill ? parseInt(formData.per_kill) : 0,
          metadata: {}
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create match');
      }

      onSuccess();
    } catch (error) {
      toast.error(error.message || 'Failed to create match');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date-time (current time + 1 hour)
  const getMinDateTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return now.toISOString().slice(0, 16);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h3 className="modal-title">Create New Match</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Match Title *</label>
            <input
              type="text"
              name="title"
              className="form-input"
              placeholder="e.g., Friday Night Tournament"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            <div className="form-group">
              <label className="form-label">Mode *</label>
              <select
                name="mode"
                className="form-input"
                value={formData.mode}
                onChange={handleChange}
                required
              >
                <option value="solo">Solo</option>
                <option value="duo">Duo</option>
                <option value="squad">Squad</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Map *</label>
              <select
                name="map"
                className="form-input"
                value={formData.map}
                onChange={handleChange}
                required
              >
                <option value="Erangel">Erangel</option>
                <option value="Miramar">Miramar</option>
                <option value="Sanhok">Sanhok</option>
                <option value="Vikendi">Vikendi</option>
                <option value="Livik">Livik</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            <div className="form-group">
              <label className="form-label">Total Slots *</label>
              <input
                type="number"
                name="slot_count"
                className="form-input"
                min="10"
                max="200"
                value={formData.slot_count}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Entry Fee (₹) *</label>
              <input
                type="number"
                name="entry_fee"
                className="form-input"
                min="0"
                value={formData.entry_fee}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              className="form-input"
              placeholder="Match rules, specific instructions, etc."
              value={formData.description}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            <div className="form-group">
              <label className="form-label">Format</label>
              <select
                name="format"
                className="form-input"
                value={formData.format}
                onChange={handleChange}
              >
                <option value="TPP">TPP</option>
                <option value="FPP">FPP</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Prize Pool (₹)</label>
              <input
                type="number"
                name="prize_pool"
                className="form-input"
                placeholder="0"
                min="0"
                value={formData.prize_pool}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Per Kill Price (₹)</label>
            <input
              type="number"
              name="per_kill"
              className="form-input"
              placeholder="0"
              min="0"
              value={formData.per_kill || ''}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Start Date & Time *</label>
            <input
              type="datetime-local"
              name="start_ts"
              className="form-input"
              min={getMinDateTime()}
              value={formData.start_ts}
              onChange={handleChange}
              required
            />
            <p style={{
              fontSize: '0.85rem',
              color: 'var(--text-secondary)',
              marginTop: '5px'
            }}>
              Must be at least 1 hour from now
            </p>
          </div>

          <div style={{
            padding: '15px',
            background: 'rgba(0, 240, 255, 0.1)',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <h4 style={{ fontSize: '1rem', marginBottom: '10px', color: 'var(--primary-color)' }}>
              Match Summary
            </h4>
            <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              <div>Title: <strong style={{ color: 'var(--text-primary)' }}>{formData.title || 'Not set'}</strong></div>
              <div>Mode: <strong style={{ color: 'var(--text-primary)' }}>{formData.mode.toUpperCase()}</strong></div>
              <div>Map: <strong style={{ color: 'var(--text-primary)' }}>{formData.map}</strong></div>
              <div>Slots: <strong style={{ color: 'var(--text-primary)' }}>{formData.slot_count}</strong></div>
              <div>Entry Fee: <strong style={{ color: 'var(--text-primary)' }}>₹{formData.entry_fee}</strong></div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={onClose}
              style={{ flex: 1 }}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-success"
              style={{ flex: 1 }}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Match'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateMatchModal;
